(() => {
  'use strict';

  const PROVIDER_PREFIX = 'manual-florist-';
  const MANUAL_PRICING_REVISION = 'manual-fallback-v1';
  const INSTALL_FLAG = '__FLORAME_MANUAL_FLORIST_CART_BRIDGE__';
  const PATCH_FLAG = '__manualFloristCartBridgePatched__';

  function text(value) {
    return String(value == null ? '' : value).trim();
  }

  function propertiesOf(item) {
    return item?.properties && typeof item.properties === 'object' && !Array.isArray(item.properties)
      ? { ...item.properties }
      : {};
  }

  function positiveCents(...values) {
    for (const value of values) {
      const numeric = Math.round(Number(value) || 0);
      if (numeric > 0) return String(numeric);
    }
    return '';
  }

  function matchedFallbackSlug(instance) {
    const state = typeof instance?.getCapStockState === 'function'
      ? instance.getCapStockState()
      : (instance?.capData || instance?.stockData || {});
    const slug = text(
      state?.matchedPartnerSlug
      || state?.assignedPartnerSlug
      || state?.fulfillment?.selectedPartner?.partnerSlug
      || state?.fulfillment?.matchedPartner?.partnerSlug
      || state?.manualFallback?.partnerSlug
    );
    return slug.startsWith(PROVIDER_PREFIX) ? slug : '';
  }

  async function jsonFetch(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {})
      }
    });
    if (!response.ok) throw new Error(`manual_florist_cart_${response.status}`);
    return response.json();
  }

  async function bindFallbackToCart(instance) {
    const partnerSlug = matchedFallbackSlug(instance);
    if (!partnerSlug) return null;

    let cart = await jsonFetch('/cart.js', { method: 'GET', headers: {} });
    const attributes = {
      ...(cart.attributes || {}),
      Partner_Source: partnerSlug,
      Partner_Slug: partnerSlug,
      Partner_Matched_Slug: partnerSlug,
      Partner_Source_Type: 'manual_fallback'
    };
    cart = await jsonFetch('/cart/update.js', {
      method: 'POST',
      body: JSON.stringify({ attributes })
    });

    for (const item of cart.items || []) {
      const properties = propertiesOf(item);
      if (String(properties._florame_delivery_auto || '') === '1') continue;
      const bouquetId = text(properties._florame_bouquet_id || item.product_id || item.variant_id);
      const listPriceCents = text(properties._florame_list_price_cents)
        || positiveCents(item.original_price, item.price, Number(item.final_line_price || 0) / Math.max(1, Number(item.quantity || 1)));
      if (!bouquetId || !listPriceCents) throw new Error('manual_florist_cart_pricing_evidence_missing');
      const nextProperties = {
        ...properties,
        _florame_partner_slug: partnerSlug,
        Partner_Source: partnerSlug,
        _florame_request_kind: 'catalog',
        _florame_bouquet_id: bouquetId,
        _florame_pricing_revision: text(properties._florame_pricing_revision) || MANUAL_PRICING_REVISION,
        _florame_list_price_cents: listPriceCents,
        _florame_pricing_mode: text(properties._florame_pricing_mode) || 'manual_fallback'
      };
      const unchanged = Object.entries(nextProperties).every(([key, value]) => (
        String(properties[key] == null ? '' : properties[key]) === String(value)
      ));
      if (unchanged) continue;
      cart = await jsonFetch('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({
          id: item.key,
          quantity: Number(item.quantity || 0),
          properties: nextProperties
        })
      });
    }

    document.dispatchEvent(new CustomEvent('florame:cart-updated', {
      detail: { reason: 'manual-florist-cart-binding', partnerSlug }
    }));
    return cart;
  }

  function install() {
    const instance = window.DS;
    if (!instance || typeof instance.confirmDelivery !== 'function') return false;
    if (instance[PATCH_FLAG]) return true;

    const original = instance.confirmDelivery.bind(instance);
    instance.confirmDelivery = async function manualFloristConfirmDelivery(...args) {
      try {
        await bindFallbackToCart(instance);
      } catch (error) {
        console.error('[ManualFloristCartBridge] cart binding failed', error);
        if (typeof instance.setVerificationStatus === 'function') {
          instance.setVerificationStatus('error', 'Non siamo riusciti a preparare il fiorista di riserva. Riprova.');
        }
        if (typeof instance.showError === 'function') {
          instance.showError(error instanceof Error ? error : new Error('manual_florist_cart_binding_failed'));
        }
        return undefined;
      }
      return original(...args);
    };
    Object.defineProperty(instance, PATCH_FLAG, { value: true, configurable: false });
    return true;
  }

  if (window[INSTALL_FLAG]) return;
  window[INSTALL_FLAG] = true;
  if (install()) return;

  const observer = new MutationObserver(() => {
    if (install()) observer.disconnect();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('load', install, { once: true });
  setTimeout(() => observer.disconnect(), 15000);
})();
